import { Box, Button, Skeleton, TextField, Typography } from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import type { SalesAgentControl } from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { salesApi } from "../client.js";

/**
 * Pause / resume button for the autonomous sales agent. Lives in the
 * Sales shell's PageHeader so it's visible from every tab.
 *
 * - Polls `/api/sales/v2/agent/control` every 30 s so the button always
 *   reflects the current state across operators.
 * - Pause shows a `<ConfirmDialog>` with an optional reason field
 *   (free-text — "end of month budget freeze", "investigating bad
 *   leads", etc.) so the team knows why the agent is quiet.
 * - Resume is a single-click action; no confirm needed.
 * - Hidden entirely when the operator lacks `sales:edit`. Read-only
 *   viewers see the paused state via the SalesHealthPill instead.
 *
 * Pause does NOT kill in-flight missions. The discovery sub-agent
 * working on a lead right now will complete; the next master tick
 * after pause is a no-op until resume. Anthropic spend stops within
 * a minute or so.
 */
export function AgentControlButton(): JSX.Element | null {
  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();

  const ctl = useQuery({
    queryKey: ["sales", "agent-control"],
    queryFn: salesApi.getAgentControl,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");

  // Pausing/resuming changes the agent's REAL status everywhere it's shown:
  // the header health pill (`sales/health`) AND the Status-tab hero (the
  // `["sales","today"]` query → `/v2/today` carries status_line + is_paused).
  // Refresh both immediately so the operator sees the flip without a manual
  // reload — the bug was that only the pill refreshed, so the hero stayed
  // "Agent paused" after Resume.
  const reflectControlChange = (state: SalesAgentControl) => {
    qc.setQueryData(["sales", "agent-control"], state);
    qc.invalidateQueries({ queryKey: ["sales", "health"] });
    qc.invalidateQueries({ queryKey: ["sales", "today"] });
  };

  const pause = useMutation({
    mutationFn: () => salesApi.pauseAgent(reason),
    onSuccess: (state: SalesAgentControl) => {
      reflectControlChange(state);
      setConfirmOpen(false);
      setReason("");
    },
  });

  const resume = useMutation({
    mutationFn: () => salesApi.resumeAgent(),
    onSuccess: reflectControlChange,
  });

  // Hide entirely for non-editors — they still see paused state via
  // the health pill.
  if (!canEdit) return null;

  // Loading-state skeleton: a placeholder pill so the header doesn't
  // shift around when the first poll lands.
  if (!ctl.data) {
    return (
      <Skeleton
        variant="rounded"
        aria-label="Loading agent control"
        sx={{ height: 28, width: 80, borderRadius: 1 }}
      />
    );
  }

  const isPaused = ctl.data.is_paused;
  const busy = pause.isPending || resume.isPending;

  return (
    <>
      <Button
        type="button"
        variant="contained"
        size="small"
        disableElevation
        disabled={busy}
        onClick={() => {
          if (isPaused) {
            resume.mutate();
          } else {
            setConfirmOpen(true);
          }
        }}
        startIcon={isPaused ? <PlayArrowIcon sx={{ fontSize: 16 }} /> : <PauseIcon sx={{ fontSize: 16 }} />}
        title={
          isPaused
            ? `Resume — paused${ctl.data.paused_reason ? `: ${ctl.data.paused_reason}` : ""}`
            : "Pause — stops new Beat-fired tasks; in-flight missions finish"
        }
        aria-label={isPaused ? "Resume agent" : "Pause agent"}
        // Paused → primary action is RESUME (positive, restores work) →
        // solid green. Running → primary action is PAUSE (stop spend) →
        // solid red so it reads as the obvious "kill switch."
        color={isPaused ? "success" : "error"}
        sx={{ fontSize: 12, fontWeight: 600, px: 1.5, py: 0.75 }}
      >
        {isPaused ? "Resume agent" : "Pause agent"}
      </Button>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          if (!pause.isPending) {
            setConfirmOpen(false);
            setReason("");
          }
        }}
        onConfirm={async () => { await pause.mutateAsync(); }}
        title="Pause sales agent?"
        confirmLabel={pause.isPending ? "Pausing…" : "Pause agent"}
        cancelLabel="Keep running"
        variant="warning"
        description="While paused, no new master ticks, missions, or feed polls will run. In-flight discovery missions finish on their own. Anthropic spend stops within a minute or so."
      >
        <Box sx={{ display: "grid", gap: 1, mt: 1 }}>
          <Typography
            component="label"
            htmlFor="agent-pause-reason"
            sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
          >
            Reason (optional, shown to the team)
          </Typography>
          <TextField
            id="agent-pause-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 2000 }}
            placeholder="e.g. budget freeze for end of month, or investigating bad-quality leads"
            disabled={pause.isPending}
            fullWidth
          />
        </Box>
      </ConfirmDialog>
    </>
  );
}
